import React, { ReactNode } from "react";
import moment, { Moment } from "moment";
import cns from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeType } from "@theme";
import Body from "../../typography/Body";
import { Grid } from "./Grid";
import { Span, RenderStyle } from "./utils";

type Props<T> = {
  cells: T[];
  cellSpacing?: number;
  contentSelector: (v: T) => ReactNode;
  isCellDisabled?: (index: number, date: Moment, color: string) => boolean;
  onMouseDown?: (e: React.SyntheticEvent<any>) => void;
  onMouseOver?: (e: React.MouseEvent) => void;
  renderCellContent?: (index: number, color: string) => string;
  readonly?: boolean;
  renderPrefix?: (v: { current: T; previous: T; next?: T }) => ReactNode;
  renderStyle: RenderStyle;
  rowHeight?: number;
  spans: Span[];
};

export function RowSpanGrid<T>(props: Props<T>) {
  const {
    cells,
    cellSpacing = 32,
    contentSelector,
    isCellDisabled = () => false,
    renderCellContent,
    onMouseDown,
    readonly,
    renderPrefix,
    rowHeight = 20,
    spans
  } = props;

  const classes = useStyles({});
  const rowHeightWithSpace = rowHeight + 1;

  return (
    <Grid<T>
      cells={cells}
      spans={spans}
      onMouseDown={onMouseDown}
      onMouseOver={props.onMouseOver}
    >
      {(cell, maxColors, index) => {
        const currentPrefix =
          renderPrefix &&
          renderPrefix({
            current: cell.cell,
            previous: cell.left ? cell.left.cell : null
          });

        const nextPrefix =
          cell.right &&
          renderPrefix &&
          renderPrefix({
            current: cell.right.cell,
            previous: cell.cell
          });

        const thisCellStartFromNewLine = cell.left && currentPrefix !== null;
        const nextCellStartFromNewLine = cell.right && nextPrefix !== null;

        return (
          <React.Fragment key={cell.key}>
            {thisCellStartFromNewLine && <div className={classes.break} />}
            <div
              className={cns(classes.cell, {
                [classes.enabled]: !readonly
              })}
              key={cell.key}
              data-cell={cell.key}
              style={{ height: maxColors * rowHeightWithSpace + 21 }}
            >
              <div style={{ display: "flex" }}>
                {renderPrefix &&
                  renderPrefix({
                    current: cell.cell,
                    previous: cell.left ? cell.left.cell : null,
                    next: cell.right ? cell.right.cell : null
                  })}

                <div className={classes.cellContent} style={{ width: cellSpacing }}>
                  <Body>{contentSelector(cell.cell)}</Body>
                </div>
              </div>

              <div className={classes.colors}>
                {cell.colors.map((x) => {
                  return (
                    <div
                      key={x.color}
                      className={cns(classes.color, {
                        [classes.disabled]: isCellDisabled(
                          index,
                          moment(cell.cell),
                          x.color
                        ),
                        [classes.start]: x.first || thisCellStartFromNewLine,
                        [classes.dataAlignRight]: x.first,
                        [classes.dataAlignCenter]: !x.first,
                        [classes.end]: x.last || nextCellStartFromNewLine
                      })}
                      style={{
                        backgroundColor: x.color,
                        top: x.index * (rowHeight + 1),
                        height: rowHeight,
                        zIndex: x.index
                      }}
                      data-index={x.index}
                    >
                      {renderCellContent(index, x.color)}
                    </div>
                  );
                })}
              </div>
            </div>
          </React.Fragment>
        ) as React.ReactNode;
      }}
    </Grid>
  );
}

const borderRadius = 5;

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    userSelect: "none",
    alignItems: "center"
  },
  start: {
    borderTopLeftRadius: borderRadius,
    borderBottomLeftRadius: borderRadius
  },
  dataAlignRight: {
    textAlign: "right",
    paddingRight: theme.spacing(1.5)
  },
  dataAlignCenter: {
    textAlign: "center"
  },
  end: {
    borderBottomRightRadius: borderRadius,
    borderTopRightRadius: borderRadius
  },
  color: {
    position: "absolute",
    width: "100%",
    transition: "all 0.3s ease"
  },

  disabled: {
    opacity: 0.5
  },

  colors: {
    position: "relative",
    width: "100%"
  },
  enabled: {
    cursor: "pointer"
  },
  cell: {
    flexDirection: "column",
    color: "lightgray",
    transition: "height 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.2)",
      borderRadius,
      color: "white"
    }
  },
  break: {
    flexBasis: "100%",
    height: 0
  },
  cellContent: {
    textAlign: "center"
  }
}));
